/*
 * Copyright (C) Contributors to the Suwayomi project
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 */

/**
 * Centralized Navigation and URL Resolver for Yomiri (Agent A).
 * Guarantees consistent URL construction, parameter normalization, and broken-route prevention.
 */

export type NavigationSection =
    | 'home'
    | 'library'
    | 'explore'
    | 'sources'
    | 'reader'
    | 'anime'
    | 'detail'
    | 'live'
    | 'history'
    | 'updates'
    | 'more'
    | 'settings'
    | 'appearance'
    | 'about'
    | 'plugins'
    | string;

export interface DetailNavigationTarget {
    section: 'detail';
    workId?: string;
    mangaId?: string;
    animeId?: string;
    sourceId?: string;
    mediaId?: number | string; // AniList external link
    title?: string;
}

export interface ReaderNavigationTarget {
    section: 'reader';
    chapterId: string;
    workId?: string;
    editionId?: string;
    mangaId?: string;
    sourceId?: string;
    page?: number;
}

export interface AnimePlayerNavigationTarget {
    section: 'anime';
    episodeId: string;
    sourceId: string;
    animeId?: string;
    workId?: string;
    editionId?: string;
}

export interface LibraryNavigationTarget {
    section: 'library';
    tab?: 'watchlist' | 'history' | 'playlists' | 'downloads' | 'anilist' | 'local' | 'stats' | string;
    type?: 'all' | 'manga' | 'anime' | 'downloads';
    status?: string;
    query?: string;
}

export interface ExploreNavigationTarget {
    section: 'explore';
    sourceId?: string;
    subTab?: 'sources' | 'extensions' | 'migrate' | string;
}

export interface GenericNavigationTarget {
    section: NavigationSection;
    [key: string]: any;
}

export type NavigationTarget =
    | DetailNavigationTarget
    | ReaderNavigationTarget
    | AnimePlayerNavigationTarget
    | LibraryNavigationTarget
    | ExploreNavigationTarget
    | GenericNavigationTarget;

export interface ResolvedRoute {
    section: string;
    workId?: string;
    editionId?: string;
    sourceId?: string;
    mangaId?: string;
    animeId?: string;
    chapterId?: string;
    episodeId?: string;
    mediaId?: number;
    tab?: string;
    query?: string;
    type?: string;
    status?: string;
    page?: number;
    rawParams: Record<string, string>;
    canonicalUrl: string;
}

/**
 * Builds a canonical Yomiri URL from a structured NavigationTarget.
 */
export function buildNavigationUrl(target: NavigationTarget): string {
    const params = new URLSearchParams();

    // Set primary section
    const section = target.section || 'home';
    params.set('section', section);

    switch (target.section) {
        case 'detail': {
            if (target.workId) params.set('work', target.workId);
            if (target.mangaId) params.set('manga', target.mangaId);
            if (target.animeId) params.set('anime', target.animeId);
            if (target.sourceId) params.set('source', target.sourceId);
            if (target.mediaId) params.set('media', String(target.mediaId));
            if (target.title) params.set('title', target.title);
            break;
        }
        case 'reader': {
            if (target.chapterId) params.set('chapter', target.chapterId);
            if (target.mangaId) params.set('manga', target.mangaId);
            if (target.sourceId) params.set('source', target.sourceId);
            if (target.workId) params.set('work', target.workId);
            if (target.editionId) params.set('edition', target.editionId);
            if (target.page !== undefined && target.page > 0) params.set('page', String(target.page));
            break;
        }
        case 'anime': {
            if (target.sourceId) {
                params.set('source', target.sourceId);
                // Also set legacy animeSource for backward compatibility with older components
                params.set('animeSource', target.sourceId);
            }
            if (target.animeId) params.set('anime', target.animeId);
            if (target.episodeId) params.set('episode', target.episodeId);
            if (target.workId) params.set('work', target.workId);
            if (target.editionId) params.set('edition', target.editionId);
            break;
        }
        case 'library': {
            if (target.tab) params.set('tab', target.tab);
            if (target.type) params.set('type', target.type);
            if (target.status) params.set('status', target.status);
            if (target.query) params.set('q', target.query);
            break;
        }
        case 'explore': {
            if (target.sourceId) params.set('source', target.sourceId);
            if (target.subTab) params.set('tab', target.subTab);
            break;
        }
        default: {
            for (const [key, value] of Object.entries(target)) {
                if (key !== 'section' && value !== undefined && value !== null && typeof value !== 'object') {
                    params.set(key, String(value));
                }
            }
            break;
        }
    }

    return `/?${params.toString()}`;
}

/**
 * Resolves and normalizes any URL, location string, or URLSearchParams into a safe canonical ResolvedRoute.
 * Automatically recovers from missing/misnamed parameters and avoids broken routes.
 */
export function resolveNavigationUrl(input?: string | URLSearchParams | Location): ResolvedRoute {
    let search = '';
    if (!input && typeof location !== 'undefined') {
        search = location.search;
    } else if (typeof input === 'string') {
        const withoutHash = input.split('#')[0];
        if (withoutHash.includes('?')) {
            search = withoutHash.slice(withoutHash.indexOf('?'));
        } else if (withoutHash.startsWith('section=') || withoutHash.includes('=')) {
            search = `?${withoutHash}`;
        } else if (withoutHash.trim()) {
            search = `?section=${withoutHash.trim()}`;
        }
    } else if (input instanceof URLSearchParams) {
        search = `?${input.toString()}`;
    } else if (input && typeof input === 'object' && 'search' in input) {
        search = (input.search || '').split('#')[0];
    }

    // Strip hash fragment before parsing params
    const cleanSearch = search.split('#')[0];
    const params = new URLSearchParams(cleanSearch);
    const rawParams: Record<string, string> = {};
    params.forEach((val, key) => { rawParams[key] = val; });

    let section = (params.get('section') || 'home').toLowerCase();
    if (section === 'manga' || section === 'mangas') section = 'explore';
    else if (section === 'animes') section = 'anime';
    else if (section === 'fuentes' || section === 'plugins') section = 'sources';
    else if (section === 'biblioteca') section = 'library';
    else if (section === 'descargas') section = 'downloads';
    else if (section === 'historial') section = 'history';
    else if (section === 'ajustes' || section === 'settings') section = 'more';

    // Parameter normalization with legacy compatibility
    const workId = params.get('work') || params.get('workId') || undefined;
    const editionId = params.get('edition') || params.get('editionId') || undefined;
    const sourceId = params.get('source') || params.get('animeSource') || params.get('sourceId') || undefined;
    const mangaId = params.get('manga') || params.get('mangaId') || undefined;
    const animeId = params.get('anime') || params.get('animeId') || undefined;
    const chapterId = params.get('chapter') || params.get('chapterId') || undefined;
    const episodeId = params.get('episode') || params.get('episodeId') || undefined;
    const mediaRaw = params.get('media') || params.get('discoveryId') || undefined;
    const mediaId = mediaRaw && !isNaN(Number(mediaRaw)) ? Number(mediaRaw) : undefined;
    const tab = params.get('tab') || undefined;
    const query = params.get('q') || params.get('query') || undefined;
    const type = params.get('type') || undefined;
    const status = params.get('status') || undefined;
    const pageRaw = params.get('page');
    const page = pageRaw && !isNaN(Number(pageRaw)) ? Number(pageRaw) : undefined;

    // Safety checks against broken routes:
    // If user accesses reader without chapterId, redirect to detail view or explore safely
    if (section === 'reader' && !chapterId) {
        section = (mangaId || workId) ? 'detail' : 'explore';
    }

    // Anime section is a first-class discovery & catalog view (retains 'anime')

    // If detail route has no target entity, fallback to library safely
    if (section === 'detail' && !workId && !mangaId && !animeId && !mediaId) {
        section = 'library';
    }

    // Build the clean canonical URL
    const canonicalParams = new URLSearchParams({ section });
    if (workId) canonicalParams.set('work', workId);
    if (editionId) canonicalParams.set('edition', editionId);
    if (sourceId) canonicalParams.set('source', sourceId);
    if (mangaId) canonicalParams.set('manga', mangaId);
    if (animeId) canonicalParams.set('anime', animeId);
    if (chapterId) canonicalParams.set('chapter', chapterId);
    if (episodeId) canonicalParams.set('episode', episodeId);
    if (mediaId) canonicalParams.set('media', String(mediaId));
    if (page !== undefined && page > 0) canonicalParams.set('page', String(page));
    if (type) canonicalParams.set('type', type);
    if (status) canonicalParams.set('status', status);
    if (tab) canonicalParams.set('tab', tab);
    if (query) canonicalParams.set('q', query);

    return {
        section,
        workId,
        editionId,
        sourceId,
        mangaId,
        animeId,
        chapterId,
        episodeId,
        mediaId,
        tab,
        query,
        type,
        status,
        page,
        rawParams,
        canonicalUrl: `/?${canonicalParams.toString()}`,
    };
}

/**
 * Performs pushState or replaceState navigation with URL normalization.
 */
export function navigateTo(target: NavigationTarget, options?: { replace?: boolean }): void {
    const url = buildNavigationUrl(target);
    if (typeof history !== 'undefined') {
        if (options?.replace) {
            history.replaceState({}, '', url);
        } else {
            history.pushState({}, '', url);
        }
        window.dispatchEvent(new PopStateEvent('popstate'));
    }
}
